import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fileUrl, API } from "../lib/api";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { TrendingUp, BarChart3, Camera, Image as ImageIcon, FileText, PenTool, Sparkles, Utensils, ExternalLink, MapPin } from "lucide-react";

const ICON_MAP = { TrendingUp, BarChart3, Camera, Image: ImageIcon, FileText, PenTool, Sparkles };

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function fireBeacon(path) {
  try {
    const url = `${API}${path}`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([], { type: "application/json" }));
    else api.post(path).catch((e) => console.warn(`Ad beacon ${path} failed:`, e));
  } catch (e) { console.warn(`Ad beacon ${path} threw:`, e); }
}

function ServiceIcon({ name, icon, dark = false }) {
  const Cmp = (icon && ICON_MAP[icon]) || Utensils;
  const cls = dark
    ? "bg-white/20 text-white border-white/30 hover:bg-white/30"
    : "bg-[var(--brand-primary)]/8 text-[var(--brand-primary)] border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/15";
  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>
        <span role="img" aria-label={name} tabIndex={0}
              className={`inline-flex items-center justify-center w-7 h-7 rounded-sm border focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/30 transition-colors ${cls}`}>
          <Cmp size={14} strokeWidth={1.75} aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="bg-[#0A0A0A] text-white text-xs px-2 py-1 rounded-sm">{name}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Renders a single ad inside its slot with proper layout per variant.
 * variant: marquee-top | hero-banner | sidebar
 */
function AdView({ ad, variant, serviceIconByTitle, onView, onClick }) {
  const ref = useRef(null);
  const fired = useRef(false);

  // For marquee, fire impression immediately on mount (deduped per session via onView).
  // Continuous scroll makes IO unreliable for the 1s @ 50% threshold.
  useEffect(() => {
    if (variant === "marquee-top" && !fired.current) {
      fired.current = true;
      onView(ad);
    }
  }, [variant, ad, onView]);

  // For non-marquee slots, use IntersectionObserver (1s @ ≥50%)
  useEffect(() => {
    if (variant === "marquee-top") return;
    if (!ref.current || fired.current) return;
    const el = ref.current;
    let timer = null;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= 0.5) {
        if (timer) return;
        timer = setTimeout(() => {
          if (!fired.current) { fired.current = true; onView(ad); }
        }, 1000);
      } else if (timer) { clearTimeout(timer); timer = null; }
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => { io.disconnect(); if (timer) clearTimeout(timer); };
  }, [variant, ad, onView]);

  const focal = ad.image_focal_point || "center center";
  // Per-slot image: sidebar uses its own vertical image; marquee + hero share the wide one.
  const imageId = variant === "sidebar"
    ? (ad.sidebar_image_id || ad.background_image_id)
    : ad.background_image_id;
  const hasBg = !!imageId;
  const bgUrl = hasBg ? fileUrl(imageId) : null;

  // Slot-specific dimensions
  const slotCls = {
    "marquee-top":   "shrink-0 w-[340px] h-[96px]",
    "hero-banner":   "w-full aspect-[16/5] sm:aspect-[16/4] min-h-[160px]",
    "sidebar":       "w-full aspect-[3/4]",
  }[variant] || "w-full aspect-video";

  const isMarquee = variant === "marquee-top";

  const Body = (
    <article ref={ref}
      data-testid={`ad-card-${ad.id}`}
      data-variant={variant}
      aria-label={`${ad.restaurant_name}: ${(ad.services || []).join(", ")}`}
      className={`relative overflow-hidden rounded-sm border border-black/10 group ${slotCls} ${isMarquee ? "mx-3" : ""} ${hasBg ? "" : "bg-[#0A0A0A]"}`}
    >
      {hasBg ? (
        <>
          <img
            src={bgUrl}
            alt={`${ad.restaurant_name} restaurant photo`}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            style={{ objectPosition: focal }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15" aria-hidden />
        </>
      ) : (
        <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#0A0A0A] to-[#2A0F12]" />
      )}

      <div className={`relative z-10 h-full flex ${isMarquee ? "items-center gap-3 px-3 py-2" : "flex-col justify-end p-4 sm:p-5 gap-2"}`}>
        {/* Logo / initials */}
        {ad.logo_image_id ? (
          <img src={fileUrl(ad.logo_image_id)} alt="" loading="lazy"
               className={`${isMarquee ? "w-10 h-10" : "w-12 h-12"} rounded-sm object-cover bg-white/10 ring-1 ring-white/30 shrink-0`} />
        ) : (
          <div aria-hidden className={`${isMarquee ? "w-10 h-10 text-sm" : "w-12 h-12 text-base"} rounded-sm bg-white/15 backdrop-blur text-white flex items-center justify-center font-bold tracking-wider ring-1 ring-white/30 shrink-0`}>
            {initials(ad.restaurant_name)}
          </div>
        )}

        <div className={`min-w-0 ${isMarquee ? "flex-1" : ""}`}>
          <h3 className={`text-white font-display font-bold leading-tight drop-shadow ${isMarquee ? "text-sm truncate" : variant === "hero-banner" ? "text-xl sm:text-2xl" : "text-lg"}`}
              title={ad.restaurant_name}>
            {ad.restaurant_name}
          </h3>
          {ad.address && (
            <span
              role="link"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(`https://maps.google.com/?q=${encodeURIComponent(ad.address)}`, "_blank", "noopener,noreferrer");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(`https://maps.google.com/?q=${encodeURIComponent(ad.address)}`, "_blank", "noopener,noreferrer");
                }
              }}
              data-testid={`ad-address-link-${ad.id}`}
              className={`mt-0.5 inline-flex items-center gap-1 cursor-pointer ${isMarquee ? "text-[10px]" : "text-[11px]"} text-white/80 hover:text-white`}
              aria-label={`Open ${ad.address} in maps`}
            >
              <MapPin size={isMarquee ? 10 : 11}/>
              <span className={`truncate ${isMarquee ? "max-w-[180px]" : "max-w-[220px]"}`}>{ad.address}</span>
            </span>
          )}
          <div className={`${isMarquee ? "mt-1" : "mt-2"} flex flex-wrap gap-1.5`} data-testid={`ad-services-${ad.id}`}>
            {(ad.services || []).slice(0, isMarquee ? 4 : 6).map((s) => (
              <ServiceIcon key={s} name={s} icon={serviceIconByTitle[s]} dark />
            ))}
            {(ad.services || []).length > (isMarquee ? 4 : 6) && (
              <span className="text-[10px] uppercase tracking-wider text-white/70 self-center">+{ad.services.length - (isMarquee ? 4 : 6)}</span>
            )}
          </div>
          {!isMarquee && ad.website_url && (
            <span className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/90 group-hover:text-white">
              Visit website <ExternalLink size={11}/>
            </span>
          )}
        </div>
      </div>
    </article>
  );

  const wrapped = (
    <Tooltip delayDuration={250}>
      <TooltipTrigger asChild>{Body}</TooltipTrigger>
      <TooltipContent side="bottom" className="bg-[#0A0A0A] text-white text-xs px-2.5 py-1.5 rounded-sm max-w-[280px]">
        <div className="font-medium">{ad.restaurant_name}</div>
        {ad.services?.length > 0 && (
          <div className="mt-1 text-[10px] uppercase tracking-wider text-white/60">{ad.services.join(" · ")}</div>
        )}
      </TooltipContent>
    </Tooltip>
  );

  if (ad.website_url) {
    return (
      <a href={ad.website_url} target="_blank" rel="noopener noreferrer sponsored"
         onClick={() => onClick(ad)} onAuxClick={() => onClick(ad)}
         className="no-underline block" data-testid={`ad-link-${ad.id}`}>
        {wrapped}
      </a>
    );
  }
  return wrapped;
}

/**
 * AdSlot — fetches and rotates ads for a given (page, slot).
 * For marquee-top, renders a continuous-scroll strip (no rotation).
 * For all other slots, rotates one at a time every 5 seconds.
 */
export default function AdSlot({ page, slot, className = "" }) {
  const [ads, setAds] = useState([]);
  const [services, setServices] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/ads?page=${page}&slot=${slot}`).then((r) => {
      if (!cancelled) setAds(r.data || []);
    }).catch(() => { if (!cancelled) setAds([]); });
    return () => { cancelled = true; };
  }, [page, slot]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  const serviceIconByTitle = useMemo(() => {
    const map = {};
    for (const s of services) map[s.title] = s.icon || "Sparkles";
    return map;
  }, [services]);

  // Rotate (non-marquee slots, more than one ad)
  useEffect(() => {
    if (slot === "marquee-top" || ads.length < 2) return;
    const i = setInterval(() => setActiveIdx((idx) => (idx + 1) % ads.length), 5000);
    return () => clearInterval(i);
  }, [slot, ads.length]);

  // Pause marquee when tab hidden
  useEffect(() => {
    const onVis = () => {
      if (!trackRef.current) return;
      trackRef.current.style.animationPlayState = document.hidden ? "paused" : "running";
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const onView = (ad) => {
    const key = `ad_seen_${ad.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    fireBeacon(`/ads/${ad.id}/view`);
  };
  const onClick = (ad) => fireBeacon(`/ads/${ad.id}/click`);

  if (ads.length === 0) return null;

  if (slot === "marquee-top") {
    // Continuous scroll, duplicate items for seamless loop
    const looped = [];
    while (looped.length < 6) looped.push(...ads);
    const items = [...looped, ...looped];
    return (
      <TooltipProvider>
        <aside data-testid="marquee-panel" aria-label="Featured restaurants" className={`bg-[#FAFAFA] border-b border-black/5 py-3 overflow-hidden ${className}`}>
          <div ref={trackRef} className="marquee-track" data-testid="marquee-track">
            {items.map((ad, i) => (
              <AdView key={`${ad.id}-${i}`} ad={ad} variant="marquee-top" serviceIconByTitle={serviceIconByTitle} onView={onView} onClick={onClick} />
            ))}
          </div>
        </aside>
      </TooltipProvider>
    );
  }

  const current = ads[activeIdx % ads.length];
  return (
    <TooltipProvider>
      <section data-testid={`ad-slot-${slot}`} data-slot={slot} data-page={page} aria-label={`Advertisement (${slot})`} className={className}>
        <div key={current.id} className="ad-fade-in">
          <AdView ad={current} variant={slot} serviceIconByTitle={serviceIconByTitle} onView={onView} onClick={onClick} />
        </div>
        {ads.length > 1 && (
          <div className="mt-2 flex items-center justify-center gap-1.5" role="tablist" aria-label="Ad rotation">
            {ads.map((a, i) => (
              <button key={a.id} type="button" data-testid={`ad-dot-${slot}-${i}`}
                      onClick={() => setActiveIdx(i)}
                      role="tab" aria-selected={i === activeIdx}
                      aria-label={`Show ad ${i + 1} of ${ads.length}`}
                      className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-6 bg-[#0A0A0A]" : "w-1.5 bg-black/20 hover:bg-black/40"}`} />
            ))}
          </div>
        )}
      </section>
    </TooltipProvider>
  );
}
